import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Bell, Menu, X } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'

const Navigation = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleLogout = () => {
    navigate('/', { replace: true })
    logout()
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // The homepage opens on a dark full-bleed hero, so the unscrolled nav sits
  // over dark artwork rather than the cream background every other page has.
  const overDarkHero = location.pathname === '/' && !scrolled

  return (
    <nav
      className={`fixed top-0 left-0 right-0 h-16 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-museum-ivory/95 backdrop-blur-md border-b border-museum-antique-gold/15'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="px-6 md:px-10 h-full flex items-center justify-between max-w-[1320px] mx-auto">
        {/* Logo */}
        <Link to={user ? '/collections' : '/'} className="flex items-center gap-2.5 group">
          <div
            className={`w-7 h-7 border flex items-center justify-center transition-colors duration-300 ${
              overDarkHero ? 'border-museum-antique-gold/60' : 'border-museum-antique-gold/50'
            }`}
          >
            <span className="text-museum-antique-gold font-editorial italic text-sm leading-none">H</span>
          </div>
          <span
            className={`font-sans font-medium uppercase tracking-[0.28em] text-xs transition-colors duration-300 ${
              overDarkHero ? 'text-museum-ivory' : 'text-museum-black'
            }`}
          >
            Heritage
          </span>
        </Link>

        {/* Primary links */}
        <div className="hidden md:flex items-center gap-9">
          {['Collections', 'Journal', 'About'].map((label) => (
            <Link
              key={label}
              to={label === 'Journal' ? '/blog' : `/${label.toLowerCase()}`}
              className={`font-sans text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                overDarkHero ? 'text-museum-ivory/70 hover:text-museum-ivory' : 'text-museum-black/60 hover:text-museum-black'
              }`}
            >
              {label}
            </Link>
          ))}
        </div>

        {/* Right section */}
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <button
                className="p-2 text-charcoal/50 hover:text-primary-500 transition-colors"
                aria-label="Notifications"
              >
                <Bell size={18} />
              </button>
              <div className="relative group">
                <div className="w-8 h-8 border border-primary-500/40 flex items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-primary-500/10 transition-all duration-300">
                  <span className="text-primary-600 font-display font-bold text-xs">
                    {user.name?.charAt(0)?.toUpperCase() || 'A'}
                  </span>
                </div>
                {/* Dropdown */}
                <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-primary-500/20 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                  <div className="p-3 border-b border-charcoal/10">
                    <p className="font-display font-bold text-charcoal uppercase tracking-widest text-xs">
                      {user.name || 'Collector'}
                    </p>
                    <p className="text-charcoal/50 font-body text-xs mt-1">{user.email}</p>
                  </div>
                  <div className="p-2">
                    <Link
                      to="/profile"
                      className="block px-3 py-2 text-charcoal/60 font-body text-sm hover:text-primary-500 hover:bg-cream transition-colors"
                    >
                      My Profile
                    </Link>
                    <Link
                      to="/dashboard"
                      className="block px-3 py-2 text-charcoal/60 font-body text-sm hover:text-primary-500 hover:bg-cream transition-colors"
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-3 py-2 text-charcoal/60 font-body text-sm hover:text-primary-500 hover:bg-cream transition-colors border-t border-charcoal/10 mt-1 pt-3"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="hidden sm:flex items-center gap-5">
              <Link
                to="/login"
                className={`font-sans text-[11px] uppercase tracking-[0.18em] border-b transition-colors duration-300 ${
                  overDarkHero
                    ? 'text-museum-ivory/80 border-museum-antique-gold/50 hover:text-museum-ivory hover:border-museum-antique-gold'
                    : 'text-museum-black/70 border-museum-antique-gold/40 hover:text-museum-black hover:border-museum-antique-gold'
                }`}
              >
                Sign In
              </Link>
            </div>
          )}

          {/* Mobile toggle */}
          <button
            className={`md:hidden hover:text-primary-500 transition-colors p-1 ml-1 ${
              overDarkHero ? 'text-white/70' : 'text-charcoal/60'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-museum-ivory border-t border-museum-antique-gold/15 px-6 py-5 space-y-0.5">
          {user ? (
            <>
              <Link to="/collections" className="block text-museum-black/70 font-sans uppercase tracking-[0.18em] text-xs hover:text-museum-antique-gold transition-colors py-3.5 border-b border-museum-black/5" onClick={() => setMobileOpen(false)}>Collections</Link>
              <Link to="/challenges" className="block text-museum-black/70 font-sans uppercase tracking-[0.18em] text-xs hover:text-museum-antique-gold transition-colors py-3.5 border-b border-museum-black/5" onClick={() => setMobileOpen(false)}>Challenges</Link>
              <Link to="/showcase" className="block text-museum-black/70 font-sans uppercase tracking-[0.18em] text-xs hover:text-museum-antique-gold transition-colors py-3.5 border-b border-museum-black/5" onClick={() => setMobileOpen(false)}>Showcase</Link>
              <button onClick={() => { handleLogout(); setMobileOpen(false) }} className="block text-museum-black/60 font-sans uppercase tracking-[0.18em] text-xs hover:text-museum-antique-gold transition-colors py-3.5 w-full text-left">Sign Out</button>
            </>
          ) : (
            <>
              <Link to="/collections" className="block text-museum-black/70 font-sans uppercase tracking-[0.18em] text-xs hover:text-museum-antique-gold transition-colors py-3.5 border-b border-museum-black/5" onClick={() => setMobileOpen(false)}>Collections</Link>
              <Link to="/blog" className="block text-museum-black/70 font-sans uppercase tracking-[0.18em] text-xs hover:text-museum-antique-gold transition-colors py-3.5 border-b border-museum-black/5" onClick={() => setMobileOpen(false)}>Journal</Link>
              <Link to="/about" className="block text-museum-black/70 font-sans uppercase tracking-[0.18em] text-xs hover:text-museum-antique-gold transition-colors py-3.5 border-b border-museum-black/5" onClick={() => setMobileOpen(false)}>About</Link>
              <Link to="/login" className="block text-museum-black/70 font-sans uppercase tracking-[0.18em] text-xs hover:text-museum-antique-gold transition-colors py-3.5" onClick={() => setMobileOpen(false)}>Sign In</Link>
            </>
          )}
        </div>
      )}
    </nav>
  )
}

export default Navigation
